import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import Sidebar from './components/Sidebar';
import HeaderBar from './components/dashboard/HeaderBar';
import LicenseBanner from './components/LicenseBanner';
import DashboardEvoPage from './pages/DashboardEvoPage';
import ExportHubPage from './pages/ExportHubPage';
import EsportabiliPage from './pages/EsportabiliPage';
import ImportPage from './pages/ImportPage';
import NumeriPage from './pages/NumeriPage';
import ExportPage from './pages/ExportPage';
import VerifichePage from './pages/VerifichePage';
import CoperturaPage from './pages/CoperturaPage';
import CoperturePage from './pages/CoperturePage';
import StrumentiPage from './pages/StrumentiPage';
import DominiRivederePage from './pages/DominiRivederePage';
import CampagnePage from './pages/CampagnePage';
import AziendeCompletarePage from './pages/AziendeCompletarePage';
import Cruscotto456Page from './pages/Cruscotto456Page';
import SsmateDashboardPage from './pages/SsmateDashboardPage';
import AgenteVfPage from './pages/AgenteVfPage';
import AgenteA456Page from './pages/AgenteA456Page';
import AnalisiPage from './pages/AnalisiPage';
import { SingleNotifProvider } from './components/SingleNotif';
import SalutePage from './pages/SalutePage';
import RicercaOnlinePage from './pages/RicercaOnlinePage';
import IndagaDashboardPage from './pages/IndagaDashboardPage';
import ImpostazioniPage from './pages/ImpostazioniPage';
import UtilizzoPage from './pages/UtilizzoPage';
import PublicDesktopPage from './pages/PublicDesktopPage';
import AgentePage from './pages/AgentePage';
import LoginPage from './pages/LoginPage';
import MobileApp from './mobile/MobileApp';
import { AuthProvider, useAuth } from './auth';
import { DashboardSelectionProvider } from './dashboardSelection';
import { CoperturaProvider } from './coperturaState';
import { SECTION_ROUTES } from './perms';
import './auth.css';

function firstAllowed(can) {
  const f = SECTION_ROUTES.find((s) => can(s.key));
  return f ? f.path : null;
}

// Protegge una rotta: se l'utente non ha il permesso, lo manda alla prima sezione consentita.
function Guard({ need, admin, children }) {
  const { can, user } = useAuth();
  // `need` puo' essere una lista: basta UNO dei permessi (pagine a tab, dove ogni tab
  // ha poi il suo gate).
  const ok = Array.isArray(need) ? need.some((k) => can(k)) : can(need);
  if (admin ? user?.ruolo === 'admin' : ok) return children;
  const dest = firstAllowed(can);
  if (dest) return <Navigate to={dest} replace />;
  return <div style={{ padding: 24, color: '#64748b' }}>Nessuna sezione abilitata per il tuo utente. Contatta l'amministratore.</div>;
}

function Shell() {
  const { user } = useAuth();
  // Installazione da DESKTOP: usa il manifest desktop (start_url "/") al posto di
  // quello agganciato in index.html (mobile, start_url "/m"). Cosi' l'app installata
  // dal desktop apre la versione desktop e non la versione smartphone.
  React.useEffect(() => {
    const el = document.querySelector('link[rel="manifest"]');
    if (el) el.setAttribute('href', '/manifest-desktop.json');
    const tc = document.querySelector('meta[name="theme-color"]');
    if (tc) tc.setAttribute('content', '#0b0c0e');
  }, []);
  if (!user) return <LoginPage />;
  return (
    <BrowserRouter>
      <DashboardSelectionProvider>
      <CoperturaProvider>
      <SingleNotifProvider>
      <div className="app-layout">
        <Sidebar />
        <div className="nav-scrim" aria-hidden="true" />
        <div className="main-wrapper">
          <LicenseBanner />
          <HeaderBar />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Guard need="dashboard"><DashboardEvoPage /></Guard>} />
              <Route path="/export-hub" element={<Guard need="export_hub"><ExportHubPage /></Guard>} />
              <Route path="/esportabili" element={<Guard need="priorita"><EsportabiliPage /></Guard>} />
              <Route path="/import" element={<Guard need="import"><ImportPage /></Guard>} />
              <Route path="/numeri" element={<Guard need="numeri"><NumeriPage /></Guard>} />
              <Route path="/export" element={<Guard need="export"><ExportPage /></Guard>} />
              <Route path="/verifiche" element={<Guard need="verifiche"><VerifichePage /></Guard>} />
              <Route path="/copertura" element={<Guard need="copertura"><CoperturaPage /></Guard>} />
              <Route path="/coperture" element={<Guard need={['agente_copertura', 'ssmate_copertura']}><CoperturePage /></Guard>} />
              {/* vecchi indirizzi: le due pagine sono confluite in /coperture */}
              <Route path="/agente-copertura" element={<Navigate to="/coperture" replace />} />
              <Route path="/copertura-ssmate" element={<Navigate to="/coperture" replace />} />
              <Route path="/strumenti" element={<Guard need="strumenti"><StrumentiPage /></Guard>} />
              <Route path="/domini-rivedere" element={<Guard need="domini_rivedere"><DominiRivederePage /></Guard>} />
              <Route path="/campagne" element={<Guard need="campagne"><CampagnePage /></Guard>} />
              <Route path="/aziende-da-completare" element={<Guard need="vicine_completare"><AziendeCompletarePage /></Guard>} />
              <Route path="/cruscotto-456" element={<Guard need="cruscotto_456"><Cruscotto456Page /></Guard>} />
              <Route path="/ssmate" element={<Guard need="ssmate"><SsmateDashboardPage /></Guard>} />
              <Route path="/agente-vf" element={<Guard need="gea"><AgenteVfPage /></Guard>} />
              <Route path="/agente-456" element={<Guard need="verifiche"><AgenteA456Page /></Guard>} />
              <Route path="/analisi" element={<Guard need="analisi"><AnalisiPage /></Guard>} />
              <Route path="/salute" element={<Guard need="salute"><SalutePage /></Guard>} />
              <Route path="/ricerca-online" element={<Guard need="ricerca_online"><RicercaOnlinePage /></Guard>} />
              <Route path="/indaga" element={<Guard need="indaga"><IndagaDashboardPage /></Guard>} />
              <Route path="/impostazioni" element={<Guard need="impostazioni"><ImpostazioniPage /></Guard>} />
              <Route path="/utilizzo" element={<Guard admin><UtilizzoPage /></Guard>} />
            </Routes>
          </main>
        </div>
      </div>
      </SingleNotifProvider>
      </CoperturaProvider>
      </DashboardSelectionProvider>
    </BrowserRouter>
  );
}

export default function App() {
  const path = window.location.pathname;
  // /pd/ è deprecato: reindirizza allo stesso token su /p/ (la pagina PD ora vive su /p).
  if (path.startsWith('/pd/')) {
    window.location.replace('/p/' + path.slice('/pd/'.length) + window.location.search);
    return null;
  }
  // Rotta pubblica link collaboratore: ora mostra la pagina PD (sostituisce la vecchia mobile).
  // Fuori dal gate di login e dall'AuthProvider. Il vecchio componente mobile resta solo per l'agente.
  if (path.startsWith('/p/')) {
    return <PublicDesktopPage />;
  }
  // Match esatto: NON deve catturare rotte staff come /agente-copertura.
  if (path === '/agente' || path.startsWith('/agente/')) {
    return <AgentePage />;
  }
  // Frontend mobile "Centralino" su /m. Solo su smartphone (touch + schermo piccolo)
  // si apre automaticamente, una sola volta. I tablet (iPad in verticale = 768px,
  // Android tablet) restano sulla versione desktop: soglia sotto la larghezza dei tablet.
  const isMobilePath = path === '/m' || path.startsWith('/m/');
  if (!isMobilePath && window.matchMedia
      && window.matchMedia('(max-width: 600px) and (pointer: coarse)').matches) {
    window.location.replace('/m');
    return null;
  }
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      {isMobilePath ? <MobileApp /> : <Shell />}
    </AuthProvider>
  );
}
